import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, topBtn } from './VendorDetails';

interface IpRow {
  id: string;
  ip_address: string;
  source: string;
  hostnames: string[];
  owner: string | null;
  asn: string | null;
  asn_name: string | null;
  ip_range_start: string | null;
  ip_range_end: string | null;
  country: string | null;
  country_code: string | null;
  services: string[];
  label: string | null;
  first_detected: string | null;
  last_seen: string | null;
}

interface Payload {
  addresses: IpRow[];
  last_check: { checked_at: string; ip_count: number } | null;
  stale: boolean;
  detecting: boolean;
}

interface Props {
  vendorId: string;
  isDark: boolean;
}

type SortKey = 'ip' | 'owner' | 'network' | 'country' | 'services';

const mono = "'JetBrains Mono', ui-monospace, monospace";

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};

const fmtDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const fmtRelative = (value: string) => {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const ipAddressesKey = (vendorId: string) => ['vendors', 'detail', vendorId, 'ip-addresses'] as const;

const ipValue = (ip: string) => ip.split('.').reduce((acc, part) => acc * 256 + (parseInt(part, 10) || 0), 0);

// Plain web ports are expected on a vendor's hosts; anything else answering on the public internet
// (remote shells, file transfer, mail relays, remote desktop) is worth a second look.
const WEB_SERVICES = new Set(['HTTPS', 'HTTP']);
const SERVICE_ORDER = ['HTTPS', 'HTTP', 'SSH', 'FTP', 'SMTP', 'RDP'];
const SERVICE_HINT: Record<string, string> = {
  HTTPS: 'Encrypted web traffic (port 443)',
  HTTP: 'Unencrypted web traffic (port 80)',
  SSH: 'Remote shell access is reachable from the internet (port 22)',
  FTP: 'File transfer is reachable from the internet (port 21)',
  SMTP: 'Mail server is reachable from the internet (port 25)',
  RDP: 'Remote desktop is reachable from the internet (port 3389)',
};

// Provider logos via the same favicon convention the Vendors list already uses. Matched on the
// owner name; anything unrecognised falls back to an initial tile.
const PROVIDER_DOMAINS: [RegExp, string][] = [
  [/google/i, 'google.com'], [/microsoft|azure/i, 'microsoft.com'], [/amazon|aws/i, 'aws.amazon.com'],
  [/hostinger/i, 'hostinger.com'], [/cloudflare/i, 'cloudflare.com'], [/fastly/i, 'fastly.com'],
  [/akamai|linode/i, 'akamai.com'], [/digitalocean/i, 'digitalocean.com'], [/ovh/i, 'ovhcloud.com'],
  [/hetzner/i, 'hetzner.com'], [/oracle/i, 'oracle.com'], [/alibaba/i, 'alibabacloud.com'],
  [/godaddy/i, 'godaddy.com'], [/vercel/i, 'vercel.com'], [/netlify/i, 'netlify.com'], [/webflow/i, 'webflow.com'],
  [/github/i, 'github.com'], [/salesforce/i, 'salesforce.com'], [/zoho/i, 'zoho.com'], [/shopify/i, 'shopify.com'],
];
const providerDomain = (owner: string | null) => PROVIDER_DOMAINS.find(([re]) => re.test(owner || ''))?.[1] || null;

const PALETTE = ['#3B82F6', '#16A34A', '#D97706', '#7C3AED', '#DB2777', '#0D9488', '#4F46E5', '#059669'];
const hashColor = (text: string) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};

function ProviderLogo({ owner, isDark, size = 30 }: { owner: string | null; isDark: boolean; size?: number }) {
  const domain = providerDomain(owner);
  const [failed, setFailed] = useState(false);
  if (domain && !failed) {
    return (
      <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} alt="" onError={() => setFailed(true)}
        style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
    );
  }
  const color = hashColor(owner || '?');
  return (
    <span style={{
      width: size, height: size, borderRadius: 7, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}1a`, color, fontSize: size * 0.42, fontWeight: 700,
    }}>
      {(owner || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

function Flag({ code, isDark }: { code: string | null; isDark: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!code) return null;
  if (failed) {
    return <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : T.slate, width: 20, display: 'inline-block' }}>{code}</span>;
  }
  return (
    <img src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`} alt={code} onError={() => setFailed(true)}
      style={{ width: 26, height: 18, objectFit: 'cover', borderRadius: 3, flexShrink: 0, boxShadow: '0 0 0 1px rgba(0,0,0,.08)' }} />
  );
}

export default function VendorIPAddresses({ vendorId, isDark }: Props) {
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';

  const queryClient = useQueryClient();
  const ipKey = ipAddressesKey(vendorId);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [exposureFilter, setExposureFilter] = useState<'all' | 'exposed' | 'web'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'ip', dir: 1 });
  const [checkError, setCheckError] = useState('');
  const [editingLabel, setEditingLabel] = useState<{ id: string; value: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const autoChecked = useRef<string | null>(null);

  const line = isDark ? '#1f1f1f' : T.bdr;
  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const accent = isDark ? '#3B82F6' : T.blue;
  const danger = '#DC2626';

  const checkMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/v1/vendors/${id}/ip-addresses/detect`)).data as Payload,
    // The check answers with the refreshed list itself, so it goes straight into the cache (no refetch needed).
    onSuccess: (payload, id) => queryClient.setQueryData(ipAddressesKey(id), payload),
  });
  const checking = checkMutation.isPending;

  const ipQuery = useQuery({
    queryKey: ipKey,
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/ip-addresses`)).data as Payload,
    // While a check started elsewhere is still running, keep refreshing.
    refetchInterval: query => (query.state.data?.detecting && !checking ? 3000 : false),
  });
  const data = ipQuery.data ?? null;
  const loading = !data && (ipQuery.isPending || ipQuery.isFetching);
  const loadError = !data && ipQuery.isError ? apiError(ipQuery.error, 'Could not load IP addresses.') : '';

  const labelMutation = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) => api.put(`/v1/vendors/${vendorId}/ip-addresses/${id}/label`, { label }),
    meta: { invalidates: [ipKey] },
  });
  const labelSaving = labelMutation.isPending;

  const runCheck = async () => {
    setCheckError('');
    try {
      await checkMutation.mutateAsync(vendorId);
    } catch (error) {
      setCheckError(apiError(error, 'Could not run the check. Try again.'));
    }
  };

  useEffect(() => {
    setSelectedId(null);
  }, [vendorId]);

  // A vendor never checked before (onboarded before this feature, or never scanned), or whose
  // last check was made by an older detector, gets checked when someone who may run one opens the tab.
  // `autoChecked` limits this to once per vendor, so background refetches can't start it again.
  useEffect(() => {
    if (!data || !canEdit || checking || data.detecting || (data.last_check && !data.stale) || autoChecked.current === vendorId) return;
    autoChecked.current = vendorId;
    runCheck();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data, canEdit, vendorId]);

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !editingLabel) setSelectedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, editingLabel]);

  const saveLabel = async () => {
    if (!editingLabel) return;
    try {
      await labelMutation.mutateAsync({ id: editingLabel.id, label: editingLabel.value.trim() || null });
      setEditingLabel(null);
    } catch (error) {
      setCheckError(apiError(error, 'Could not save the label.'));
    }
  };

  const rows = useMemo(() => data?.addresses || [], [data]);
  const isExposed = (row: IpRow) => row.services.some(s => !WEB_SERVICES.has(s));

  const stats = useMemo(() => {
    const count = (pick: (r: IpRow) => string | null) => {
      const map = new Map<string, number>();
      rows.forEach(r => { const key = pick(r); if (key) map.set(key, (map.get(key) || 0) + 1); });
      return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    };
    return {
      providers: count(r => r.owner),
      countries: count(r => r.country),
      networks: new Set(rows.map(r => r.asn).filter(Boolean)).size,
      exposed: rows.filter(isExposed).length,
      unencrypted: rows.filter(r => r.services.includes('HTTP') && !r.services.includes('HTTPS')).length,
      countryCodes: new Map(rows.filter(r => r.country).map(r => [r.country as string, r.country_code])),
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = rows.filter(row => {
      if (providerFilter && row.owner !== providerFilter) return false;
      if (countryFilter && row.country !== countryFilter) return false;
      if (exposureFilter === 'exposed' && !isExposed(row)) return false;
      if (exposureFilter === 'web' && isExposed(row)) return false;
      if (!needle) return true;
      return [row.ip_address, row.owner, row.asn, row.asn_name, row.country, row.label, ...row.hostnames, ...row.services]
        .some(value => (value || '').toLowerCase().includes(needle));
    });
    const keyOf = (r: IpRow): string | number => {
      switch (sort.key) {
        case 'ip': return ipValue(r.ip_address);
        case 'owner': return (r.owner || '').toLowerCase();
        case 'network': return (r.asn_name || r.asn || '').toLowerCase();
        case 'country': return (r.country || '').toLowerCase();
        case 'services': return r.services.length;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      return (ka < kb ? -1 : ka > kb ? 1 : 0) * sort.dir || ipValue(a.ip_address) - ipValue(b.ip_address);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rows, search, providerFilter, countryFilter, exposureFilter, sort]);

  const selected = useMemo(() => rows.find(row => row.id === selectedId) || null, [rows, selectedId]);
  const busy = checking || !!data?.detecting;
  const filtersActive = !!(search || providerFilter || countryFilter || exposureFilter !== 'all');

  const clearFilters = () => { setSearch(''); setProviderFilter(''); setCountryFilter(''); setExposureFilter('all'); };

  const exportCsv = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = ['IP Address', 'Source', 'Owner', 'ASN', 'Network', 'Range Start', 'Range End', 'Country', 'Services', 'Hosts', 'Label'];
    const body = visible.map(r => [
      r.ip_address, r.source, r.owner || '', r.asn || '', r.asn_name || '', r.ip_range_start || '', r.ip_range_end || '',
      r.country || '', r.services.join(' '), r.hostnames.join(' '), r.label || '',
    ].map(v => escape(String(v))).join(','));
    const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ip-addresses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── styles ─────────────────────────────────────────────────────── */
  const serviceChip = (service: string): React.CSSProperties => {
    const color = service === 'HTTPS' ? '#16A34A' : service === 'HTTP' ? '#D97706' : '#DC2626';
    return {
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 6,
      whiteSpace: 'nowrap', color, background: `${color}${isDark ? '22' : '12'}`, border: `1px solid ${color}40`, fontFamily: mono,
    };
  };
  const dot = (color: string): React.CSSProperties => ({ width: 6, height: 6, borderRadius: 99, background: color, flexShrink: 0 });
  const tag = (color: string): React.CSSProperties => ({
    display: 'inline-block', fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
    whiteSpace: 'nowrap', color, border: `1px solid ${color}55`, background: `${color}14`,
  });
  const headCell = (key?: SortKey, width?: number | string): React.CSSProperties => ({
    padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
    color: sort.key === key ? ink : slate, background: surface2, borderBottom: `1px solid ${line}`,
    cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap', width,
    position: 'sticky', top: 0, zIndex: 1,
  });
  const cell: React.CSSProperties = { padding: '16px', fontSize: 14, color: ink, borderBottom: `1px solid ${isDark ? '#1a1a1a' : '#EEF2F6'}`, verticalAlign: 'middle' };
  const selectStyle: React.CSSProperties = {
    padding: '8px 30px 8px 12px', fontSize: 13, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`,
    borderRadius: 8, outline: 'none', cursor: 'pointer', appearance: 'none', maxWidth: 220,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394A3B8' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 11px center',
  };
  const segment = (active: boolean): React.CSSProperties => ({
    padding: '7px 14px', fontSize: 13, fontWeight: 600, fontFamily: sans, cursor: 'pointer', border: 'none', borderRadius: 6,
    background: active ? surface : 'transparent', color: active ? ink : slate,
    boxShadow: active ? (isDark ? '0 0 0 1px #262626' : '0 1px 2px rgba(15,23,42,.08), 0 0 0 1px #E2E8F0') : 'none',
  });
  const sectionLabel: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: slate };

  const SortHead = ({ k, label, width }: { k: SortKey; label: string; width?: number | string }) => (
    <th style={headCell(k, width)} onClick={() => setSort(s => ({ key: k, dir: s.key === k ? (s.dir === 1 ? -1 : 1) : 1 }))}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        {label}
        <span style={{ fontSize: 9, opacity: sort.key === k ? 1 : 0.35 }}>{sort.key === k && sort.dir === -1 ? '▼' : '▲'}</span>
      </span>
    </th>
  );

  const renderLabel = (row: IpRow, large = false) => {
    if (editingLabel?.id === row.id) {
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            autoFocus value={editingLabel.value} disabled={labelSaving} placeholder="e.g. Production web"
            onChange={e => setEditingLabel({ id: row.id, value: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') saveLabel(); if (e.key === 'Escape') setEditingLabel(null); }}
            style={{
              width: large ? '100%' : 130, flex: large ? 1 : undefined, padding: large ? '8px 10px' : '5px 8px', fontSize: large ? 15 : 12.5,
              fontFamily: sans, color: ink, background: surface2, border: `1px solid ${accent}`, borderRadius: 6, outline: 'none',
            }}
          />
          <button onClick={saveLabel} disabled={labelSaving} style={{ ...topBtn(true, isDark), padding: large ? '7px 14px' : '5px 10px', fontSize: large ? 13.5 : 12 }}>
            Save
          </button>
        </div>
      );
    }
    if (row.label) {
      return (
        <span role={canEdit ? 'button' : undefined} title={canEdit ? 'Edit label' : undefined}
          onClick={() => canEdit && setEditingLabel({ id: row.id, value: row.label || '' })}
          style={{ ...tag('#7C3AED'), fontSize: large ? 13 : 11.5, cursor: canEdit ? 'pointer' : 'default' }}>
          {row.label}
        </span>
      );
    }
    if (!canEdit) return <span style={{ color: muted }}>—</span>;
    return (
      <span role="button" onClick={() => setEditingLabel({ id: row.id, value: '' })}
        style={{ fontSize: large ? 13.5 : 12, color: slate, border: `1px dashed ${isDark ? '#333' : T.faint}`, borderRadius: 99, padding: large ? '4px 12px' : '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        + Add label
      </span>
    );
  };

  /* ── loading / error ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ fontFamily: sans }}>
        <style>{'@keyframes ipShimmer { 0% { opacity: .55 } 50% { opacity: 1 } 100% { opacity: .55 } }'}</style>
        <div style={{ height: 22, width: 160, borderRadius: 6, background: surface2, marginBottom: 20, animation: 'ipShimmer 1.2s infinite' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ ...card(isDark), height: 84, animation: 'ipShimmer 1.2s infinite' }} />)}
        </div>
        <div style={{ ...card(isDark), height: 320, animation: 'ipShimmer 1.2s infinite' }} />
      </div>
    );
  }
  if (loadError) {
    return (
      <div style={{ ...card(isDark), padding: 32, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 6 }}>Couldn't load IP addresses</div>
        <div style={{ fontSize: 13, color: slate, marginBottom: 14 }}>{loadError}</div>
        <button onClick={() => ipQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
      </div>
    );
  }

  const kpis = [
    { label: 'IP addresses', value: rows.length, note: data?.last_check ? `Checked ${fmtRelative(data.last_check.checked_at)}` : 'Not checked yet' },
    { label: 'Networks (ASNs)', value: stats.networks, note: stats.networks === 1 ? 'Single network' : 'Distinct networks' },
    { label: 'Hosting providers', value: stats.providers.length, note: stats.providers[0] ? `Mostly ${stats.providers[0][0]}` : '—' },
    { label: 'Countries', value: stats.countries.length, note: stats.countries.map(c => c[0]).slice(0, 2).join(', ') || '—' },
    {
      label: 'Non-web services exposed', value: stats.exposed,
      note: stats.exposed ? 'SSH, FTP, SMTP or RDP reachable' : 'Only web ports answer',
      tone: stats.exposed ? '#DC2626' : '#16A34A',
    },
  ];


  return (
    <div style={{ fontFamily: sans }}>
      <style>{'@keyframes ipDrawerSlideIn { from { transform: translateX(16px); opacity: 0; } to { transform: translateX(0); opacity: 1; } } @keyframes ipSpin { to { transform: rotate(360deg) } }'}</style>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: ink, letterSpacing: '-.01em' }}>IP Addresses</div>
          <div style={{ fontSize: 13.5, color: slate, marginTop: 4, maxWidth: 620, lineHeight: 1.5 }}>
            Every public address this vendor's hosts resolve to, with the network that owns it, where it is located and which common services answer on it.
          </div>
          {stats.unencrypted > 0 && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: '#B45309', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={dot('#D97706')} />
              {stats.unencrypted} address{stats.unencrypted === 1 ? '' : 'es'} answer{stats.unencrypted === 1 ? 's' : ''} on plain HTTP only, with no HTTPS.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {data?.last_check && (
            <span title={fmtDateTime(data.last_check.checked_at)} style={{ fontSize: 12.5, color: slate, display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
              <span style={dot(busy ? '#D97706' : '#16A34A')} />
              {busy ? 'Checking…' : `Last checked ${fmtRelative(data.last_check.checked_at)}`}
            </span>
          )}
          <button onClick={exportCsv} disabled={!visible.length} style={topBtn(false, isDark)}>Export CSV</button>
          {canEdit && (
            <button onClick={runCheck} disabled={busy} style={topBtn(true, isDark)}>
              {busy && <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: 99, animation: 'ipSpin .7s linear infinite' }} />}
              {busy ? 'Checking' : 'Check now'}
            </button>
          )}
        </div>
      </div>

      {checkError && (
        <div style={{ fontSize: 13, color: danger, background: isDark ? '#2a0f0f' : T.redBg, border: `1px solid ${isDark ? '#4a1a1a' : T.redBdr}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          {checkError}
        </div>
      )}

      {/* ── KPI strip ───────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 14 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...card(isDark), padding: '16px 18px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: slate, marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.tone || ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
            <div style={{ fontSize: 12, color: muted, marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={k.note}>{k.note}</div>
          </div>
        ))}
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 340 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={muted} strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              placeholder="Search address, owner, ASN, host…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px 8px 32px', fontSize: 13, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 8, outline: 'none' }}
            />
          </div>
          <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 8, background: surface2, border: `1px solid ${line}` }}>
            <button style={segment(exposureFilter === 'all')} onClick={() => setExposureFilter('all')}>All</button>
            <button style={segment(exposureFilter === 'exposed')} onClick={() => setExposureFilter('exposed')}>Non-web exposed</button>
            <button style={segment(exposureFilter === 'web')} onClick={() => setExposureFilter('web')}>Web only</button>
          </div>
          <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} style={selectStyle}>
            <option value="">All providers</option>
            {stats.providers.map(([name]) => <option key={name} value={name}>{name}</option>)}
          </select>
          <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} style={selectStyle}>
            <option value="">All countries</option>
            {stats.countries.map(([name]) => <option key={name} value={name}>{name}</option>)}
          </select>
          {filtersActive && (
            <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
              Clear filters
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: slate, fontVariantNumeric: 'tabular-nums' }}>
            {visible.length === rows.length ? `${rows.length} address${rows.length === 1 ? '' : 'es'}` : `${visible.length} of ${rows.length}`}
          </span>
        </div>

        {visible.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: surface2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={slate} strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" /></svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 4 }}>
              {filtersActive ? 'No addresses match these filters' : busy ? 'Looking up this vendor\'s addresses…' : 'No IP addresses yet'}
            </div>
            <div style={{ fontSize: 13, color: slate }}>
              {filtersActive ? <span role="button" onClick={clearFilters} style={{ color: accent, cursor: 'pointer' }}>Clear filters</span>
                : busy ? 'Resolving every known host and looking up who owns each address.'
                : 'This vendor may not have been scanned yet, or none of its hosts resolve to a public address.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <SortHead k="ip" label="IP address" />
                  <SortHead k="owner" label="Owner" />
                  <SortHead k="network" label="Network" />
                  <SortHead k="country" label="Location" />
                  <SortHead k="services" label="Services" />
                  <th style={headCell()}>Label</th>
                  <th style={{ ...headCell(), width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map(row => {
                  const active = row.id === selectedId;
                  return (
                    <tr
                      key={row.id} onClick={() => setSelectedId(row.id)}
                      style={{ cursor: 'pointer', background: active ? (isDark ? '#111827' : T.blueBg) : 'transparent', transition: 'background .12s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = surface2; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ ...cell, boxShadow: active ? `inset 3px 0 0 ${accent}` : undefined }}>
                        <div style={{ fontFamily: mono, fontSize: 14, fontWeight: 600, color: ink }}>{row.ip_address}</div>
                        <div style={{ fontSize: 11.5, color: muted, marginTop: 3 }}>
                          via {row.source} · {row.hostnames.length} host{row.hostnames.length === 1 ? '' : 's'}
                        </div>
                      </td>
                      <td style={cell}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <ProviderLogo owner={row.owner} isDark={isDark} />
                          <span style={{ fontWeight: 500 }}>{row.owner || '—'}</span>
                        </div>
                      </td>
                      <td style={cell}>
                        {row.asn ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: mono, fontSize: 11.5, fontWeight: 600, color: slate, background: surface2, border: `1px solid ${line}`, borderRadius: 5, padding: '1px 6px' }}>
                                {row.asn}
                              </span>
                              <span style={{ fontSize: 13, color: ink, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.asn_name || ''}>
                                {(row.asn_name || '').split(' - ')[0].split(',')[0]}
                              </span>
                            </div>
                            {row.ip_range_start && row.ip_range_end && (
                              <div style={{ fontFamily: mono, fontSize: 11.5, color: muted, marginTop: 4 }}>{row.ip_range_start} – {row.ip_range_end}</div>
                            )}
                          </>
                        ) : <span style={{ color: muted }}>—</span>}
                      </td>
                      <td style={cell}>
                        {row.country ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Flag code={row.country_code} isDark={isDark} />
                            <span>{row.country}</span>
                          </div>
                        ) : <span style={{ color: muted }}>—</span>}
                      </td>
                      <td style={cell}>
                        {row.services.length > 0 ? (
                          <div style={{ display: 'flex', gap: 5 }}>
                            {[...row.services].sort((a, b) => SERVICE_ORDER.indexOf(a) - SERVICE_ORDER.indexOf(b)).map(s => (
                              <span key={s} style={serviceChip(s)} title={SERVICE_HINT[s]}>{s}</span>
                            ))}
                          </div>
                        ) : <span style={{ fontSize: 12.5, color: muted }}>None answering</span>}
                      </td>
                      <td style={cell} onClick={e => e.stopPropagation()}>{renderLabel(row)}</td>
                      <td style={{ ...cell, color: muted, textAlign: 'right', paddingRight: 18 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail drawer ───────────────────────────────────────── */}
      {selected && (
        <>
          <div onClick={() => setSelectedId(null)} style={{ position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,.35)', zIndex: 1200 }} />
          <aside style={{
            position: 'fixed', top: 64, right: 0, bottom: 0, width: 560, maxWidth: '100%', zIndex: 1201,
            background: surface, borderLeft: `1px solid ${line}`, overflowY: 'auto', animation: 'ipDrawerSlideIn .18s ease-out',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '24px 26px 20px', borderBottom: `1px solid ${line}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                  <ProviderLogo owner={selected.owner} isDark={isDark} size={44} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: mono, fontSize: 22, fontWeight: 700, color: ink, wordBreak: 'break-all' }}>{selected.ip_address}</div>
                    <div style={{ fontSize: 14, color: slate, marginTop: 3 }}>{selected.owner || 'Unknown owner'}{selected.country ? ` · ${selected.country}` : ''}</div>
                  </div>
                </div>
                <button onClick={() => setSelectedId(null)} aria-label="Close" title="Close (Esc)" style={{
                  background: surface2, border: `1px solid ${line}`, color: slate, cursor: 'pointer', width: 32, height: 32, borderRadius: 8,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
                <span style={tag('#3B82F6')}>{selected.source}</span>
                {isExposed(selected) && <span style={tag('#DC2626')}>Non-web service exposed</span>}
                {selected.services.includes('HTTP') && !selected.services.includes('HTTPS') && <span style={tag('#D97706')}>HTTP only</span>}
              </div>
            </div>

            <div style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 24 }}>
              <section>
                <div style={{ ...sectionLabel, marginBottom: 12 }}>Network</div>
                <div style={{ border: `1px solid ${line}`, borderRadius: 10, overflow: 'hidden' }}>
                  {([
                    ['Owner', selected.owner || '—'],
                    ['Autonomous system', selected.asn ? `${selected.asn_name || '—'}` : '—'],
                    ['ASN', selected.asn || '—'],
                    ['IP range', selected.ip_range_start && selected.ip_range_end ? `${selected.ip_range_start} – ${selected.ip_range_end}` : '—'],
                    ['Location', selected.country || '—'],
                  ] as [string, string][]).map(([k, v], i) => (
                    <div key={k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, padding: '12px 16px', borderTop: i ? `1px solid ${line}` : 'none', background: i % 2 ? 'transparent' : surface2 }}>
                      <span style={{ fontSize: 13.5, color: slate }}>{k}</span>
                      <span style={{ fontSize: 15, color: ink, fontFamily: k === 'ASN' || k === 'IP range' ? mono : sans, display: 'flex', alignItems: 'center', gap: 8, wordBreak: 'break-word' }}>
                        {k === 'Location' && <Flag code={selected.country_code} isDark={isDark} />}
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <div style={{ ...sectionLabel, marginBottom: 12 }}>Services answering</div>
                {selected.services.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...selected.services].sort((a, b) => SERVICE_ORDER.indexOf(a) - SERVICE_ORDER.indexOf(b)).map(s => (
                      <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: `1px solid ${line}`, borderRadius: 10 }}>
                        <span style={{ ...serviceChip(s), minWidth: 52, justifyContent: 'center' }}>{s}</span>
                        <span style={{ fontSize: 14, color: ink }}>{SERVICE_HINT[s] || s}</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: 14.5, color: slate }}>None of the common ports checked (443, 80, 22, 21, 25, 3389) answered.</div>}
              </section>

              <section>
                <div style={{ ...sectionLabel, marginBottom: 12 }}>Hosts resolving here ({selected.hostnames.length})</div>
                {selected.hostnames.length > 0 ? (
                  <div style={{ border: `1px solid ${line}`, borderRadius: 10, overflow: 'hidden' }}>
                    {selected.hostnames.map((h, i) => (
                      <div key={h} style={{ padding: '11px 16px', fontFamily: mono, fontSize: 14, color: ink, borderTop: i ? `1px solid ${line}` : 'none' }}>{h}</div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: 14.5, color: slate }}>—</div>}
                <div style={{ fontSize: 13, color: muted, marginTop: 10, lineHeight: 1.5 }}>
                  The address itself isn't scored. Findings for these hosts are on the vendor's Domains and scan pages.
                </div>
              </section>

              <section>
                <div style={{ ...sectionLabel, marginBottom: 12 }}>Label</div>
                {renderLabel(selected, true)}
              </section>

              {(selected.first_detected || selected.last_seen) && (
                <section style={{ display: 'flex', gap: 28, fontSize: 13.5, color: slate, borderTop: `1px solid ${line}`, paddingTop: 18 }}>
                  {selected.first_detected && <div><div style={{ ...sectionLabel, fontSize: 10.5, marginBottom: 4 }}>First seen</div><span style={{ color: ink }}>{fmtDateTime(selected.first_detected)}</span></div>}
                  {selected.last_seen && <div><div style={{ ...sectionLabel, fontSize: 10.5, marginBottom: 4 }}>Last seen</div><span style={{ color: ink }}>{fmtDateTime(selected.last_seen)}</span></div>}
                </section>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
